import axios from 'axios';
import { RSAOAEPEncryption } from '../utils/rsaEncryption';

// 회원가입 API 타입 정의 (새로운 API 명세에 맞게 수정)
export interface SignupRequest {
  email: string;
  name: string;                    // 본인인증에서 받은 이름
  birth: string;                   // 본인인증에서 받은 생년월일 (YYYY-MM-DD)
  phone: string;                   // 본인인증에서 받은 전화번호
  encryptedPassword: string;       // RSA 암호화된 비밀번호
  role: 'MEMBER' | 'LECTURER';     // 역할
  identity: 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER'; // 신분
  department: string;              // 의대생: 대학교 이름, 의사: 진료과
  idCardFile?: File;               // 의대생의 경우 필수
  licenseNumber?: string;          // 의사의 경우 필수
  requiredAgreed: boolean;         // 필수 약관 동의
  marketingAgreed?: boolean;       // 선택 약관 동의
}

export interface SignupCompletionResponse {
  code: number;
  status: string;
  message: string;
  data?: {
    id: number;
    name: string;
    email: string;
    marketing_agreed: boolean;
    identity: string;
    role: string;
  };
}

// API 베이스 URL (개발 서버 직접 호출)
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://medaicampus.kr/gateway';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 세션 쿠키 포함
});

// multipart/form-data용 axios 인스턴스
const formDataApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// 전역 RSA-OAEP 인스턴스
const rsaEncryption = new RSAOAEPEncryption();

/**
 * RSA 공개키 조회 API
 * GET /api/auth/public-key
 */
export const getPublicKey = async (): Promise<string> => {
  try {
    // /// console.log('공개키 요청:', `${API_BASE_URL}/api/auth/public-key`);
    
    const response = await apiClient.get('/api/auth/public-key');
    
    /// console.log('공개키 응답 성공.');
    
    let publicKey = '';
    
    // data에서 공개키 확인
    if (response.data.data) {
      publicKey = response.data.data;
    } 
    // data가 null이면 message에서 [] 안의 공개키 추출
    else if (response.data.message) {
      const match = response.data.message.match(/\[(.*?)\]/);
      if (match && match[1]) {
        publicKey = match[1];
        /// console.log('message에서 공개키 추출 성공');
      }
    }
    
    if (!publicKey) {
      throw new Error('공개키를 찾을 수 없습니다.');
    }
    
    // /// console.log({publicKey})
    // /// console.log('공개키 추출 완료:', publicKey.substring(0, 50) + '...');
    return publicKey;
  } catch (error: any) {
    /// console.error('공개키 조회 실패:', error);
    throw new Error('공개키 조회에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
  }
};

/**
 * 비밀번호 암호화 (로그인과 동일한 방식 사용)
 */
export const encryptPassword = async (password: string): Promise<string> => {
  try {
    // 공개키 가져오기
    const publicKeyData = await getPublicKey();
    
    // 공개키 설정 (Web Crypto API용)
    await rsaEncryption.setPublicKey(publicKeyData);
    
    // 비밀번호 RSA-OAEP 암호화
    const encryptedPassword = await rsaEncryption.encryptData(password);
    
    /// console.log('비밀번호 암호화 성공');
    return encryptedPassword;
  } catch (error: any) {
    /// console.error('비밀번호 암호화 실패:', error);
    throw error;
  }
};

/**
 * 회원가입 API (새로운 명세에 맞게 수정)
 * POST /api/auth/sign-up
 * 
 * @param signupData - 회원가입 정보
 */
export const completeSignup = async (
  signupData: SignupRequest
): Promise<SignupCompletionResponse> => {
  try {
    // FormData 생성
    const formData = new FormData();
    
    // 기본 필드 추가
    formData.append('email', signupData.email);
    formData.append('name', signupData.name);
    formData.append('birth', signupData.birth);
    formData.append('phone', signupData.phone);
    formData.append('encryptedPassword', signupData.encryptedPassword);
    formData.append('role', signupData.role);
    formData.append('identity', signupData.identity);
    formData.append('department', signupData.department);
    formData.append('requiredAgreed', signupData.requiredAgreed.toString());
    
    // 선택적 필드 추가
    if (signupData.licenseNumber) {
      formData.append('licenseNumber', signupData.licenseNumber);
    }
    
    if (signupData.marketingAgreed !== undefined) {
      formData.append('marketingAgreed', signupData.marketingAgreed.toString());
    }
    
    // 파일 추가 (의대생의 경우)
    if (signupData.idCardFile) {
      formData.append('idCardFile', signupData.idCardFile);
    }

    //  console.log('회원가입 API 호출:', {
    //   formDataEntries: Array.from(formData.entries()).map(([key, value]) => [
    //     key, 
    //     value instanceof File ? `File: ${value.name}` : value
    //   ])
    // });

    const response = await formDataApiClient.post('/api/auth/sign-up', formData);
    return response.data;
  } catch (error) {
    /// console.error('회원가입 API 호출 실패:', error);
    throw error;
  }
};

/**
 * 이메일 중복 확인 API (선택사항 - 실제 API가 있다면)
 * GET /api/auth/check-email?email={email}
 */
export const checkEmailDuplication = async (email: string): Promise<{ available: boolean }> => {
  try {
    const response = await apiClient.get(`/api/auth/sign-up/check-email?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    /// console.error('이메일 중복 확인 API 호출 실패:', error);
    throw error;
  }
};
